import React, { useState, useEffect, useRef, useMemo } from 'react';
import Modal from '../components/common/Modal';
import { DateRange } from 'react-date-range';
import { AlertCircle, CalendarDays, Search, Plus, Sparkles, X, CheckCircle2, XCircle, Clock, Loader2, RefreshCw } from 'lucide-react';
import { formatDate, formatToYMD } from '../utils';
import InvoiceCard from '../components/common/InvoiceCard';
import InvoiceForm from '../components/forms/InvoiceForm';
import SearchInput from '../components/common/SearchInput';
import { LoadingErrorState } from '../components/LoadingErrorState';
import { fetchClientList, fetchInvoices } from '../hooks/queries';
import { sortByOptions } from '../constants';
import { Invoice } from '../types/invoice';
import { BtnPrimary, BtnDefault } from '../components/common/Buttons';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../state/auth';
import Select, { SelectOption } from '../components/common/Select';
import { GeneratedInvoice, InvoiceError } from '../api/invoice/generateBrandInvoices';
import {
  startClientInvoiceGeneration,
  getGenerationJobStatus,
  listGenerationJobs,
  JobProgress,
  JobStatus,
} from '../api/invoice/asyncInvoiceGeneration';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

type ClientGenerationResult = {
  period: {
    month: string;
    startDate?: string;
    endDate?: string;
    daysInMonth?: number;
  };
  summary: {
    totalClientsProcessed: number;
    successfullyGenerated: number;
    errors: number;
    totalInvoiceAmount: number;
  };
  invoices: GeneratedInvoice[];
  errors: InvoiceError[];
  filter?: {
    type: 'all' | 'specific_clients';
    clientIds?: string[];
  };
};

const ClientInvoice: React.FC = () => {
  const { dateRange, setDateRange } = useAuthStore();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<ClientGenerationResult | null>(null);
  const [selectedClients, setSelectedClients] = useState<SelectOption[]>([]);

  // Async generation state
  const [generationJobId, setGenerationJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generation history state
  const [generationHistory, setGenerationHistory] = useState<JobStatus[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [selectedMonthForRegeneration, setSelectedMonthForRegeneration] = useState<{month: number, year: number, existingJobId?: string} | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [sortBy, setSortBy] = useState<
    'billDate-asc' | 'billDate-desc' | 'totalAmount-asc' | 'totalAmount-desc'
  >('billDate-desc');

  const [page, setPage] = useState(1);
  const limit = 15;

  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: new Date(dateRange[0].startDate),
      endDate: new Date(dateRange[0].endDate),
      key: 'selection',
    },
  ]);

  const { data: clientListData, isLoading: isClientListLoading } = fetchClientList({
    enabled: isGenerateModalOpen,
  });

  const clientOptions = useMemo(() => {
    if (!clientListData?.clients?.length) return [];

    return clientListData.clients
      .filter((client) => client?._id)
      .map((client) => ({
        value: client._id as string,
        label: client.name || client.businessName || client.email || (client._id as string),
      }));
  }, [clientListData]);

  const clientNameMap = useMemo(() => {
    if (!clientListData?.clients?.length) return {};
    return clientListData.clients.reduce<Record<string, string>>((acc, client) => {
      if (client?._id) {
        acc[client._id as string] =
          client.name || client.businessName || client.email || (client._id as string);
      }
      return acc;
    }, {});
  }, [clientListData]);

  const selectedClientIds = selectedClients.map((client) => client.value);

  const resultFilterLabel = generationResult
    ? generationResult.filter?.type === 'specific_clients'
      ? `${generationResult.filter?.clientIds?.length || 0} specific clients`
      : 'All clients'
    : '';

  const resultFilterNames =
    generationResult?.filter?.type === 'specific_clients'
      ? (generationResult.filter?.clientIds || [])
          .slice(0, 3)
          .map((id) => clientNameMap[id] || id)
      : [];

  const resetGenerationModalState = () => {
    setIsGenerateModalOpen(false);
    setGenerationResult(null);
    setGenerationJobId(null);
    setJobStatus(null);
    setJobProgress(null);
    stopPolling();
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth(new Date().getMonth() + 1);
    setShowRegenerateConfirm(false);
    setSelectedMonthForRegeneration(null);
    setSelectedClients([]);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const { startDate, endDate } = tempDateRange[0];
    if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
      setDateRange([
        {
          startDate: formatToYMD(startDate),
          endDate: formatToYMD(endDate),
          key: 'selection',
        },
      ]);
      setPage(1);
      setIsDatePickerOpen(false);
    }
  }, [tempDateRange]);

  const [sortField, sortOrder] = sortBy.split('-');

  const {
    data: invoicesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = fetchInvoices({
    invoiceFor: 'client',
    startDate: formatToYMD(dateRange[0]?.startDate),
    endDate: formatToYMD(dateRange[0]?.endDate),
    search: debouncedSearch,
    page,
    limit,
    sortBy: sortField,
    sortOrder: sortOrder,
  });

  const invoices = invoicesData?.invoices || [];
  const pagination = invoicesData?.pagination;

  // Fetch generation history when modal opens
  useEffect(() => {
    if (isGenerateModalOpen) {
      fetchGenerationHistory();
    }
  }, [isGenerateModalOpen, selectedYear]);

  // const handleNewInvoice = () => {
  //   setEditingInvoice(null);
  //   setIsModalOpen(true);
  // };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsModalOpen(true);
  };

  const clearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
  };

  // Fetch generation history for the selected year
  const fetchGenerationHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await listGenerationJobs({
        year: selectedYear,
        invoiceType: 'client', // API might use 'brand' or 'client'
        limit: 100 // Get all jobs for the year
      });
      setGenerationHistory(response.jobs || []);
    } catch (error) {
      console.error('Failed to fetch generation history:', error);
      toast.error('Failed to load generation history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Get generation status for a specific month
  const getMonthGenerationStatus = (month: number) => {
    const monthJobs = generationHistory.filter(job => {
      // Check both root level and period object for month/year
      const jobMonth = job.month || (typeof job.period === 'object' && job.period?.month);
      const jobYear = job.year || (typeof job.period === 'object' && job.period?.year);
      // Also check if invoiceType is 'client' or 'brand'
      const isClientInvoice = job.invoiceType === 'client' || job.invoiceType === 'brand';
      return jobMonth === month && jobYear === selectedYear && isClientInvoice;
    });

    if (monthJobs.length === 0) return null;

    // Get the most recent job - handle multiple date field locations
    return monthJobs.sort((a, b) => {
      const aDate = a.timing?.createdAt || a.createdAt || '';
      const bDate = b.timing?.createdAt || b.createdAt || '';
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    })[0];
  };

  const handleGenerateInvoices = async (regenerate: boolean = false) => {
    setIsGenerating(true);
    setGenerationResult(null);
    setGenerationJobId(null);
    setJobStatus(null);
    setJobProgress(null);

    try {
      // Start async generation with regenerate flag if needed
      const job = await startClientInvoiceGeneration(selectedYear, selectedMonth, {
        regenerate: regenerate,
        clientIds: selectedClientIds.length ? selectedClientIds : undefined,
      });
      setGenerationJobId(job.jobId);
      setJobStatus(job.status);

      toast.success(`Invoice ${regenerate ? 'regeneration' : 'generation'} started! Job ID: ${job.jobId.slice(-8)}`);

      // Start polling for status
      startPolling(job.jobId);

      // Clear regeneration state
      setShowRegenerateConfirm(false);
      setSelectedMonthForRegeneration(null);

    } catch (error: any) {
      if (error.status === 409) {
        // Job already in progress
        const existingJob = error.data?.data;
        toast('Generation already in progress for this period', { icon: '⚠️' });
        if (existingJob?.jobId) {
          setGenerationJobId(existingJob.jobId);
          startPolling(existingJob.jobId);
        }
      } else {
        toast.error(error.data?.message || error.message || 'Failed to start invoice generation');
        setIsGenerating(false);
      }
    }
  };

  const startPolling = (jobId: string) => {
    setIsPolling(true);

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await getGenerationJobStatus(jobId);

        setJobStatus(status.status);
        setJobProgress(status.progress);

        if (status.status === 'completed') {
          // Generation completed
          stopPolling();

          const periodInfo =
            typeof status.period === 'object' ? status.period : undefined;
          const periodLabel =
            typeof status.period === 'string'
              ? status.period
              : periodInfo?.monthName ||
                (status.month && status.year
                  ? `${status.month}/${status.year}`
                  : 'Selected Period');
          const periodStartDate = periodInfo?.startDate || status.startDate;
          const periodEndDate = periodInfo?.endDate || status.endDate;
          const summary = status.summary || {};
          const progress = status.progress || {};
          const invoices =
            status.invoices || status.results?.generatedInvoices || [];
          const errors = status.errors || status.results?.errors || [];
          const filterType =
            status.metadata?.filterType === 'specific_clients'
              ? 'specific_clients'
              : 'all';
          const filterClientIds =
            status.metadata?.clientIds?.map((id) => String(id)) || [];

          setGenerationResult({
            period: {
              month: periodLabel,
              startDate: periodStartDate,
              endDate: periodEndDate,
              daysInMonth: periodInfo?.daysInMonth,
            },
            summary: {
              totalClientsProcessed:
                summary.totalProcessed || progress.processed || 0,
              successfullyGenerated:
                summary.successfullyGenerated || progress.successful || 0,
              errors: summary.errors || progress.failed || 0,
              totalInvoiceAmount: summary.totalInvoiceAmount || status.totalAmount || 0,
            },
            invoices,
            errors,
            filter:
              filterType === 'specific_clients'
                ? {
                    type: 'specific_clients',
                    clientIds: filterClientIds,
                  }
                : {
                    type: 'all',
                  },
          });
          toast.success('Invoice generation completed!');
          refetch(); // Refresh invoice list
          fetchGenerationHistory();
          setIsGenerating(false);

        } else if (status.status === 'failed') {
          // Generation failed
          stopPolling();
          toast.error('Invoice generation failed. Check job details for more information.');
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling even if one request fails
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading || isFetching) {
    return (
      <LoadingErrorState
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        data={invoicesData}
      />
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex py-4 px-4 gap-4 items-center flex-shrink-0">
        <h1 className="text-2xl font-bold mr-auto text-gray-900 dark:text-gray-100">
          Client Invoices
        </h1>

        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          onClear={clearSearch}
          placeholder="Search invoices..."
          width="300px"
        />

        <BtnDefault
          onClick={() => setIsGenerateModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Generate Invoices
        </BtnDefault>

        {/* <BtnPrimary
          onClick={handleNewInvoice}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create
        </BtnPrimary> */}

        <div className="flex gap-2 items-center">
          {/* Date Range */}
          <div className="relative">
            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-600 text-sm"
            >
              <CalendarDays className="w-4 h-4" />
              <span className="bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-md">
                {formatDate(dateRange[0]?.startDate)}
              </span>
              <span>-</span>
              <span className="bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-md">
                {formatDate(dateRange[0]?.endDate)}
              </span>
            </button>

            {isDatePickerOpen && (
              <div className="absolute z-20 top-full right-0 mt-2">
                <DateRange
                  onChange={(item) => setTempDateRange([item.selection])}
                  ranges={tempDateRange}
                  editableDateInputs
                  moveRangeOnFirstSelection={false}
                  className="shadow-lg border border-gray-200 rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as any);
              setPage(1);
            }}
            className="pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 text-sm text-gray-800 dark:text-white"
          >
            {sortByOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Invoice List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {invoices.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
            {invoices.map((invoice: Invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onEdit={handleEditInvoice}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No invoices found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex justify-center items-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700 dark:bg-zinc-900 flex-shrink-0">
          <button
            disabled={!pagination.hasPrevPage}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-4 py-1 text-sm border border-greyFifty rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-blackTen">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            disabled={!pagination.hasNextPage}
            onClick={() =>
              setPage((p) => Math.min(p + 1, pagination.totalPages))
            }
            className="px-4 py-1 text-sm border border-greyFifty rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Invoice Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        title={editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
        contentClassName="p-0"
        width="fit-content"
      >
        <InvoiceForm
          invoice={editingInvoice}
          onSuccess={() => {
            setIsModalOpen(false);
            setEditingInvoice(null);
            toast.success(
              `Invoice ${editingInvoice ? 'updated' : 'created'} successfully`
            );
          }}
          onError={(error) => toast.error(error.message)}
          isEditing={!!editingInvoice}
          isAgent={false}
        />
      </Modal>

      {/* Generate Invoices Modal */}
      <Modal
        isOpen={isGenerateModalOpen}
        onRequestClose={resetGenerationModalState}
        title={`Generate Client Invoices - ${selectedYear}`}
        width="1200px"
      >
        <div className="p-6">
          {!generationResult && !jobStatus ? (
            <div className="space-y-6">
              {/* Year Selection */}
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(Number(e.target.value));
                    fetchGenerationHistory();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 text-gray-800 dark:text-white"
                >
                  {[2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Month Grid with Generation Status */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  Select Month to Generate
                </h3>
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading generation history...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = i + 1;
                      const monthName = new Date(selectedYear, i).toLocaleDateString('en-US', { month: 'long' });
                      const monthJob = getMonthGenerationStatus(month);
                      const isSelected = selectedMonth === month;
                      const isCurrentMonth = new Date().getMonth() + 1 === month && new Date().getFullYear() === selectedYear;

                      return (
                        <div
                          key={month}
                          onClick={() => {
                            if (monthJob?.status === 'processing') {
                              toast('Generation already in progress for this month', {
                                icon: '⚠️',
                              });
                              return;
                            }
                            setSelectedMonth(month);
                            if (monthJob?.status === 'completed') {
                              setSelectedMonthForRegeneration({ month, year: selectedYear, existingJobId: monthJob._id || monthJob.jobId });
                            } else {
                              setSelectedMonthForRegeneration(null);
                            }
                          }}
                          className={`
                            relative p-4 rounded-lg border-2 cursor-pointer transition-all
                            ${isSelected
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }
                            ${monthJob?.status === 'processing' ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {monthName}
                            </span>
                            {isCurrentMonth && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                                Current
                              </span>
                            )}
                          </div>

                          {monthJob && (
                            <div className="space-y-2 text-xs">
                              {/* Status Badge */}
                              <div className="flex items-center gap-2">
                                {monthJob.status === 'completed' && (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    <span className="text-green-600 dark:text-green-400">Generated</span>
                                  </>
                                )}
                                {monthJob.status === 'processing' && (
                                  <>
                                    <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />
                                    <span className="text-yellow-600 dark:text-yellow-400">
                                      Processing ({monthJob.progress?.percentage || 0}%)
                                    </span>
                                  </>
                                )}
                                {monthJob.status === 'failed' && (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span className="text-red-600 dark:text-red-400">Failed</span>
                                  </>
                                )}
                                {monthJob.status === 'pending' && (
                                  <>
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-600 dark:text-gray-400">Pending</span>
                                  </>
                                )}
                              </div>

                              {/* Additional Info */}
                                  {monthJob.status === 'completed' && (
                                    <div className="text-gray-500 dark:text-gray-400">
                                      <div>{monthJob.summary?.successfullyGenerated || monthJob.results?.generatedInvoices?.length || monthJob.progress?.successful || 0} clients</div>
                                      {(monthJob.timing?.completedAt || monthJob.completedAt || monthJob.processingCompletedAt) && (
                                        <div>{new Date(monthJob.timing?.completedAt || monthJob.completedAt || monthJob.processingCompletedAt || '').toLocaleDateString()}</div>
                                      )}
                                      {monthJob.requestedBy?.email && (
                                        <div className="truncate">{monthJob.requestedBy.email}</div>
                                      )}
                                      {monthJob.metadata?.filterType === 'specific_clients' && (
                                        <div>Filter: Specific clients ({monthJob.metadata?.clientIds?.length || 0})</div>
                                      )}
                                    </div>
                                  )}

                              {monthJob.status === 'processing' && monthJob.progress && (
                                <div className="text-gray-500 dark:text-gray-400">
                                  <div>{monthJob.progress.processed} / {monthJob.progress.total}</div>
                                </div>
                              )}

                              {monthJob.status === 'failed' && (
                                <div className="text-gray-500 dark:text-gray-400">
                                  <div>{monthJob.summary?.errors || monthJob.results?.errors?.length || monthJob.progress?.failed || 0} errors</div>
                                </div>
                              )}

                              {/* Regenerate Option */}
                              {monthJob.status === 'completed' && isSelected && (
                                <button
                                  className="mt-2 flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowRegenerateConfirm(true);
                                  }}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Regenerate
                                </button>
                              )}
                            </div>
                          )}

                          {!monthJob && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <Plus className="w-4 h-4 text-gray-400 mb-1" />
                              Not generated
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Client Filter (optional)
                </label>
                <Select<SelectOption>
                  isMulti
                  isClearable
                  isDisabled={isClientListLoading}
                  options={clientOptions}
                  value={selectedClients}
                  onChange={(value) => setSelectedClients((value as SelectOption[]) || [])}
                  placeholder="Process all clients"
                  classNamePrefix="react-select"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Leave empty to generate invoices for every client.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-between items-center">
                <div className="text-sm text-gray-500 space-y-1">
                  {selectedMonthForRegeneration && (
                    <span className="block text-yellow-600 dark:text-yellow-400">
                      ⚠️ This will replace existing invoices for {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long' })}
                    </span>
                  )}
                  <span>
                    Targeting:{' '}
                    {selectedClientIds.length
                      ? `${selectedClientIds.length} selected client${selectedClientIds.length > 1 ? 's' : ''}`
                      : 'All clients'}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={resetGenerationModalState}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-600"
                    disabled={isGenerating}
                  >
                    Cancel
                  </button>
                  <BtnPrimary
                    onClick={() => {
                      if (selectedMonthForRegeneration) {
                        setShowRegenerateConfirm(true);
                      } else {
                        handleGenerateInvoices();
                      }
                    }}
                    disabled={isGenerating || !selectedMonth}
                    className="flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        {selectedMonthForRegeneration ? 'Regenerate' : 'Generate'}
                      </>
                    )}
                  </BtnPrimary>
                </div>
              </div>
            </div>
          ) : jobStatus && jobStatus !== 'completed' ? (
            // Progress view
            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                </div>

                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {jobStatus === 'pending' ? 'Preparing...' : 'Generating Client Invoices...'}
                </h3>

                {jobProgress && (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Processing {jobProgress.processed} of {jobProgress.total} clients
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${jobProgress.percentage || 0}%` }}
                      />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{jobProgress.successful}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Successful</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{jobProgress.failed}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                      </div>
                    </div>

                    {jobProgress.currentEntity && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Currently processing: {jobProgress.currentEntity}
                      </div>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  You can close this modal and continue working. The generation will continue in the background.
                </p>

                <button
                  onClick={() => {
                    setIsGenerateModalOpen(false);
                    toast.info('Generation continues in background');
                  }}
                  className="mt-6 px-4 py-2 bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-600"
                >
                  Close (Continue in Background)
                </button>
              </div>
            </div>
          ) : generationResult ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Generation Results - {generationResult.period.month}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Filter: {resultFilterLabel}
                    {resultFilterNames.length > 0 && (
                      <>
                        {' '}
                        (
                        {resultFilterNames.join(', ')}
                        {generationResult.filter?.clientIds &&
                          generationResult.filter.clientIds.length > resultFilterNames.length
                          ? '...'
                          : ''}
                        )
                      </>
                    )}
                  </p>
                </div>
                <button
                  onClick={resetGenerationModalState}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Processed</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {generationResult.summary.totalClientsProcessed}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Successfully Generated</div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {generationResult.summary.successfullyGenerated}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Errors</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {generationResult.summary.errors}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Amount</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(
                      generationResult.summary.totalInvoiceAmount,
                      generationResult.invoices[0]?.currency || 'USD'
                    )}
                  </div>
                </div>
              </div>

              {generationResult.invoices.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Generated Invoices ({generationResult.invoices.length})
                  </h4>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Invoice #</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Client</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Amount</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Agents</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generationResult.invoices.map((invoice, idx) => (
                          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-zinc-800">
                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{invoice.clientName}</td>
                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100 font-medium">
                              {formatCurrency(invoice.amount, invoice.currency)}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{invoice.totalAgents}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{invoice.totalBillableHours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {generationResult.errors.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    Errors ({generationResult.errors.length})
                  </h4>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Client Email</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generationResult.errors.map((error, idx) => (
                          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-zinc-800">
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{error.clientEmail}</td>
                            <td className="px-4 py-2 text-red-600 dark:text-red-400">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <BtnPrimary onClick={resetGenerationModalState}>
                  Close
                </BtnPrimary>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Regeneration Confirmation Modal */}
      <Modal
        isOpen={showRegenerateConfirm}
        onRequestClose={() => setShowRegenerateConfirm(false)}
        title="Confirm Invoice Regeneration"
        width="500px"
      >
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                  Are you sure you want to regenerate invoices for{' '}
                  {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}?
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action will:
                </p>
                <ul className="mt-2 text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                  <li>Delete all existing client/brand invoices for this period</li>
                  <li>Generate new invoices with updated data</li>
                  <li>Reset all payment statuses</li>
                  <li>Send new notification emails to brands</li>
                </ul>
                {selectedMonthForRegeneration && (
                  <p className="mt-3 text-sm font-medium text-yellow-600 dark:text-yellow-400">
                    This cannot be undone!
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={() => setShowRegenerateConfirm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-600"
            >
              Cancel
            </button>
            <BtnPrimary
              onClick={() => {
                handleGenerateInvoices(true);
                setShowRegenerateConfirm(false);
              }}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate Invoices
            </BtnPrimary>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientInvoice;
